import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Globe } from 'lucide-react';
import { CollapsibleSection, KPICard } from './IntelligenceUIComponents';
import { ResponsiveContainer, ComposedChart, XAxis, YAxis, Tooltip, Area, Line } from 'recharts';

export const DigitalMediaSection = ({ data }) => {
  const dmi = data?.digitalMediaIntelligence;
  if (!dmi) return null;
  
  const ig = dmi.socialPresence.instagram;
  const tw = dmi.socialPresence.twitter;
  const li = dmi.socialPresence.linkedin;
  const mm = dmi.mediaMetrics;
  const sa = dmi.searchAnalytics;
  const nv = dmi.narrativeVolatility;
  
  return (
    <CollapsibleSection title="Digital & Media Intelligence" icon={Globe} defaultOpen={true} badge="Live Metrics">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Social Presence</h3>
          <div className="space-y-3">
            <SocialRow icon="IG" name="Instagram" followers={ig.followers} growth={ig.monthlyGrowth} verified={ig.verified} />
            {tw.note ? (
              <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-sm">X</span>
                  <span className="text-sm font-medium">X (Twitter)</span>
                </div>
                <span className="text-xs text-muted-foreground italic">{tw.note}</span>
              </div>
            ) : (
              <SocialRow icon="X" name="X (Twitter)" followers={tw.followers} growth={tw.monthlyGrowth} />
            )}
            {li.note ? (
              <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-sm">in</span>
                  <span className="text-sm font-medium">LinkedIn</span>
                </div>
                <span className="text-xs text-muted-foreground italic">{li.note}</span>
              </div>
            ) : (
              <SocialRow icon="in" name="LinkedIn" followers={li.connections} growth={li.profileViews} />
            )}
          </div>
        </div>

        <div>
          <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Media Metrics (30 Days)</h3>
          <div className="grid grid-cols-2 gap-3">
            <KPICard label="Mentions" value={mm.mentionsLast30Days.toLocaleString()} sublabel={mm.mentionsTrend} color="stable" />
            <KPICard label="Sentiment" value={mm.sentimentScore} sublabel="Composite Score" />
            <KPICard label="Wikipedia" value={sa.wikipediaPageviews.toLocaleString()} sublabel={sa.wikipediaTrend} />
            <KPICard label="Google Trends" value={sa.googleTrendsIndex} sublabel={sa.searchVolumeTrend} />
          </div>
        </div>
      </div>

      <div className="mt-6">
        <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-3">12-Month Media Evolution</h3>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={dmi.evolutionData}>
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 10 }} />
              <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Area yAxisId="left" type="monotone" dataKey="mentions" fill="hsl(var(--primary))" fillOpacity={0.2} stroke="hsl(var(--primary))" />
              <Line yAxisId="right" type="monotone" dataKey="sentiment" stroke="hsl(var(--stable-green))" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6 p-4 bg-warning/5 border border-warning/20 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium">Media Narrative Volatility</h3>
          <Badge className={nv.level === 'Moderate' ? 'bg-warning/20 text-warning' : 'bg-stable/20 text-stable'}>{nv.level}</Badge>
        </div>
        <div className="h-2 bg-muted/30 rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${nv.level === 'Moderate' ? 'bg-warning' : 'bg-stable'}`} style={{ width: `${nv.score}%` }} />
        </div>
      </div>
    </CollapsibleSection>
  );
};

const SocialRow = ({ icon, name, followers, growth, verified }) => (
  <div className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
    <div className="flex items-center gap-2">
      <span>{icon}</span>
      <span className="text-sm font-medium">{name}</span>
      {verified && <Badge className="bg-blue-500/20 text-blue-400 text-xs">Verified</Badge>}
    </div>
    <div className="text-right">
      <p className="text-sm font-semibold">{followers.toLocaleString()}</p>
      <p className="text-xs text-stable">+{growth}%</p>
    </div>
  </div>
);

export default DigitalMediaSection;
